import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Loader2 } from "lucide-react";
import { getCompanyName } from "../lib/textron-data-processor";
import { useEngagement } from "@/contexts/EngagementContext";

// ─── Date helpers ────────────────────────────────────────────────────────────
const parseDate = (dateStr: string): Date => {
  const [day, month, year] = dateStr.split("/");
  const fullYear = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
  return new Date(fullYear, parseInt(month) - 1, parseInt(day));
};

const findAllDates = (data: any): string[] => {
  const dates = Object.keys(data).filter((k) => /^\d{2}\/\d{2}\/\d{2}$/.test(k));
  if (dates.length === 0) return [];
  dates.sort((a, b) => parseDate(b).getTime() - parseDate(a).getTime());
  return dates;
};

// ─── Shared formatter ────────────────────────────────────────────────────────
const formatCrores = (value: number): string => {
  const crores = value / 10000000;
  return `₹${Math.abs(crores).toFixed(2)} Cr`;
};

const formatVariancePct = (v1: number, v2: number): string => {
  if (v2 === 0) return v1 === 0 ? "0.00%" : "N/A";
  const pct = ((v1 - v2) / Math.abs(v2)) * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
};

// ─── AS interfaces ───────────────────────────────────────────────────────────
// Using any-indexed map so analytics key doesn't conflict with date-string entries
type BalanceSheetData = {
  [key: string]: any;
};

interface BalanceSheetSectionProps {
  searchTerm: string;
}

interface BalanceSheetItem {
  name: string;
  key?: string;
  values: { [date: string]: number };
  isTotal?: boolean;
  isSubItem?: boolean;
  level?: number;
  cagr?: number | null;
  trend?: string | null;
}

interface BalanceSheetSection {
  title: string;
  items: BalanceSheetItem[];
  total: BalanceSheetItem;
}

// ─── Ind AS row types ────────────────────────────────────────────────────────
type IndASRowType =
  | "sectionHeader"   // I. Assets / II. Equity & Liabilities
  | "subGroupHeader"  // 1. Non-Current Assets / h. Financial Assets
  | "lineItem"        // regular data row
  | "subTotal"        // XXX total for a section (no name shown in left cell)
  | "grandTotal"      // TOTAL ASSETS / TOTAL LIABILITIES
  | "blank";          // empty spacer row

interface IndASRow {
  name: string;
  key?: string;
  type: IndASRowType;
  level: number;
  values: { [date: string]: number };
}

// ─── Sparkline (AS UI only) ──────────────────────────────────────────────────
function TrendSparkline({
  values,
  datesChronological,
  trend,
  isLiability,
}: {
  values: { [date: string]: number };
  datesChronological: string[];
  trend: string | null | undefined;
  isLiability?: boolean;
}) {
  const points = datesChronological
    .map((d) => values[d])
    .filter((v): v is number => typeof v === "number");
  if (points.length < 2) return <span className="text-white/50">—</span>;

  const w = 56, h = 20, padding = 2;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const xStep = (w - padding * 2) / (points.length - 1);
  const pathD = points
    .map((val, i) => {
      const x = padding + i * xStep;
      const y = h - padding - ((val - min) / range) * (h - padding * 2);
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  const stroke =
    trend === "Stable"
      ? "rgba(255,255,255,0.9)"
      : isLiability
        ? trend === "Up" ? "#ef4444" : "#22c55e"
        : trend === "Up" ? "#22c55e" : "#ef4444";

  const uid = React.useId().replace(/:/g, "");
  const filterId = `bs-sparkline-${uid}`;
  const markerId = `bs-sparkline-arrow-${uid}`;

  return (
    <svg width={w} height={h} className="inline-block" aria-hidden>
      <defs>
        <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <marker
          id={markerId}
          markerUnits="userSpaceOnUse"
          markerWidth="8"
          markerHeight="6"
          refX="6"
          refY="3"
          orient="auto"
        >
          <path d="M 0 0 L 6 3 L 0 6 Z" fill={stroke} />
        </marker>
      </defs>
      <g filter={`url(#${filterId})`}>
        <path
          d={pathD}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          markerEnd={`url(#${markerId})`}
        />
      </g>
    </svg>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function BalanceSheetSection({ searchTerm }: BalanceSheetSectionProps) {
  const { currentEngagement } = useEngagement();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [frf, setFrf] = useState<string>("");

  // AS-specific state
  const [dates, setDates] = useState<string[]>([]);
  const [equityAndLiabilities, setEquityAndLiabilities] = useState<BalanceSheetSection | null>(null);
  const [assets, setAssets] = useState<BalanceSheetSection | null>(null);

  // Ind AS-specific state
  const [indAsDates, setIndAsDates] = useState<string[]>([]);
  const [indAsAssetRows, setIndAsAssetRows] = useState<IndASRow[]>([]);
  const [indAsLiabilityRows, setIndAsLiabilityRows] = useState<IndASRow[]>([]);

  // ── Data loader ──────────────────────────────────────────────────────────
  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const companyNameResult = await getCompanyName("combined");
      setCompanyName(companyNameResult);

      if (!(window as any).sharePointAPI?.downloadClientJson) {
        throw new Error("SharePoint API not available");
      }

      // ── Step 1: run get_frf.py → fetch EngagementDetails.json from SharePoint
      //    and read the frf field (the authoritative source of truth)
      const frfResult = await (window as any).sharePointAPI.getFrf();
      const detectedFrf: string = frfResult?.frf || "";
      setFrf(detectedFrf);
      console.log("[BalanceSheet] FRF from SharePoint:", detectedFrf);

      // ── Step 2: fetch FinData_Formulae.json ──────────────────────────────
      const finResult = await (window as any).sharePointAPI.downloadClientJson(
        "FinData_Formulae.json"
      );
      if (!finResult?.success || !finResult.data) {
        throw new Error(finResult?.error || "Failed to download financial data");
      }
      const data = finResult.data as BalanceSheetData;

      const allDates = findAllDates(data);
      if (allDates.length === 0) throw new Error("No valid dates found in financial data");

      const getValue = (key: string, date: string): number =>
        data[date]?.bs_as?.[key] || 0;

      // ════════════════════════════════════════════════════════════════════
      // Ind AS path – full BalanceSheetSection + CAGR/trend (same rendering as AS)
      // ════════════════════════════════════════════════════════════════════
      if (detectedFrf === "Indian Accounting Standards (Ind AS)") {
        setDates(allDates);

        // Helper: build a BalanceSheetItem from a bs_as key
        const mkItem = (name: string, key?: string, isSubItem = false, isTotal = false, level = 1): BalanceSheetItem => {
          const values: { [date: string]: number } = {};
          allDates.forEach((d) => { values[d] = key ? getValue(key, d) : 0; });
          return { name, key, values, isSubItem, isTotal, level };
        };
        // Helper: header item with no data (shows "—" in value cells)
        const mkHeader = (name: string, level = 1): BalanceSheetItem => mkItem(name, undefined, false, false, level);
        // Helper: total item whose value comes directly from a JSON key
        const mkTotal = (name: string, key: string, level = 1): BalanceSheetItem => mkItem(name, key, false, true, level);

        // ── Equity & Liabilities (Ind AS) ────────────────────────────────
        const ialSF     = mkTotal("1.  Shareholders' Funds", "Shareholder Fund", 1);
        const ialSC     = mkItem("a.  Share Capital", "Share Capital", true, false, 2);
        const ialOE     = mkItem("b.  Other Equity", "Other Equity", true, false, 2);

        const ialNcLiab = mkTotal("2.  Non-Current Liabilities", "Non-Current Liabilities", 1);
        const ialNcFinH = mkHeader("a.  Financial liabilities", 2);
        const ialBrNc   = mkItem("(i)    Borrowings", "Borrowings - Non Current", true, false, 3);
        const ialLeNc   = mkItem("(ia)   Lease liabilities", "Lease liabilities - Non Current", true, false, 3);
        const ialTpNcH  = mkHeader("(ii)   Trade Payables", 3);
        const ialMsNc   = mkItem("(A)      total outstanding dues of MSME; and", "Total outstanding dues of MSME - Non Current", true, false, 4);
        const ialNmNc   = mkItem("(B)      total outstanding dues of creditors other than MSME", "Total outstanding dues of creditors other than MSME - Non Current", true, false, 4);
        const ialOfNc   = mkItem("(iii)  Other financial liabilities", "Other financial liabilities - Non Current", true, false, 3);
        const ialPrNc   = mkItem("b.  Provisions", "Provisions - Non Current", true, false, 2);
        const ialDTL    = mkItem("c.  Deferred Tax Liabilities", "Deferred Tax Liabilities", true, false, 2);
        const ialONcL   = mkItem("d.  Other non-current liabilities", "Other non-current liabilities", true, false, 2);

        const ialCFinH  = mkHeader("a.  Financial liabilities", 2);
        const ialBrC    = mkItem("(i)    Borrowings", "Borrowings - Current", true, false, 3);
        const ialLeC    = mkItem("(ia)   Lease liabilities", "Lease liabilities - Current", true, false, 3);
        const ialTpCH   = mkHeader("(ii)   Trade Payables", 3);
        const ialMsC    = mkItem("(A)      total outstanding dues of MSME; and", "Total outstanding dues of MSME - Current", true, false, 4);
        const ialNmC    = mkItem("(B)      total outstanding dues of creditors other than MSME", "Total outstanding dues of creditors other than MSME - Current", true, false, 4);
        const ialOfC    = mkItem("(iii)  Other financial liabilities", "Other financial liabilities - Current", true, false, 3);
        const ialOCL    = mkItem("b.  Other current liabilities", "Other current liabilities", true, false, 2);
        const ialPrC    = mkItem("c.  Provisions", "Provisions - Current", true, false, 2);
        const ialCTL    = mkItem("d.  Current tax liabilities", "Current tax liabilities", true, false, 2);

        const currentLiabilitiesValuesInd: { [date: string]: number } = {};
        allDates.forEach((date) => {
          currentLiabilitiesValuesInd[date] =
            getValue("Borrowings - Current", date) +
            getValue("Lease liabilities - Current", date) +
            getValue("Total outstanding dues of MSME - Current", date) +
            getValue("Total outstanding dues of creditors other than MSME - Current", date) +
            getValue("Other financial liabilities - Current", date) +
            getValue("Other current liabilities", date) +
            getValue("Provisions - Current", date) +
            getValue("Current tax liabilities", date);
        });
        const ialCLiab: BalanceSheetItem = {
          name: "3.  Current Liabilities",
          values: currentLiabilitiesValuesInd,
          isTotal: true,
          level: 1,
        };

        const ialTotLiab = mkTotal("TOTAL LIABILITIES", "Total Liabilities", 1);

        const equityAndLiabilitiesItems: BalanceSheetItem[] = [
          ialSF, ialSC, ialOE,
          ialNcLiab, ialNcFinH, ialBrNc, ialLeNc, ialTpNcH, ialMsNc, ialNmNc, ialOfNc, ialPrNc, ialDTL, ialONcL,
          ialCLiab, ialCFinH, ialBrC, ialLeC, ialTpCH, ialMsC, ialNmC, ialOfC, ialOCL, ialPrC, ialCTL,
        ];

        // ── Assets (Ind AS) ──────────────────────────────────────────────
        const ialNcAssets = mkTotal("1.  Non-Current Assets", "Non-Current Assets", 1);
        const ialPPE      = mkItem("a.  Property, Plant & Equipment", "Property, Plant & Equipment", true, false, 2);
        const ialCWIP     = mkItem("b.  Capital work-in Progress", "Capital work-in Progress", true, false, 2);
        const ialInvProp  = mkItem("c.  Investment Property", "Investment Property", true, false, 2);
        const ialGW       = mkItem("d.  Goodwill", "Goodwill", true, false, 2);
        const ialOIA      = mkItem("e.  Other intangible assets", "Other intangible assets", true, false, 2);
        const ialIUD      = mkItem("f.  Intangible assets under development", "Intangible assets under development", true, false, 2);
        const ialBio      = mkItem("g.  Biological assets other than bearer plants", "Biological assets other than bearer plants", true, false, 2);
        const ialNcFinAH  = mkHeader("h.  Financial Assets", 2);
        const ialInvNc    = mkItem("(i)    Investments", "Investments - Non Current", true, false, 3);
        const ialTrRNc    = mkItem("(ii)   Trade Receivables", "Trade Receivables - Non Current", true, false, 3);
        const ialLoNc     = mkItem("(iii)  Loans", "Loans - Non Current", true, false, 3);
        const ialOtNc     = mkItem("(iv)   Others", "Others - Non Current", true, false, 3);
        const ialDTA      = mkItem("i.  Deferred Tax Assets", "Deferred Tax Assets", true, false, 2);
        const ialONcA     = mkItem("j.  Other non-current assets", "Other non-current assets", true, false, 2);

        const ialInv      = mkItem("a.  Inventories", "Inventories", true, false, 2);
        const ialCFinAH   = mkHeader("b.  Financial Assets", 2);
        const ialInvC     = mkItem("(i)    Investments", "Investments - Current", true, false, 3);
        const ialTrRC     = mkItem("(ii)   Trade Receivables", "Trade Receivables - Current", true, false, 3);
        const ialCash     = mkItem("(iii)  Cash & Cash Equivalents", "Cash & Cash Equivalents", true, false, 3);
        const ialBankBal  = mkItem("(iv)   Bank balances other than above", "Bank balances other than above", true, false, 3);
        const ialLoC      = mkItem("(v)    Loans", "Loans - Current", true, false, 3);
        const ialOtC      = mkItem("(vi)   Others", "Others - Current", true, false, 3);
        const ialCTA      = mkItem("c.  Current Tax Assets", "Current Tax Assets", true, false, 2);
        const ialOCA      = mkItem("d.  Other Current Assets", "Other Current Assets", true, false, 2);

        const currentAssetsValuesInd: { [date: string]: number } = {};
        allDates.forEach((date) => {
          currentAssetsValuesInd[date] =
            getValue("Inventories", date) +
            getValue("Investments - Current", date) +
            getValue("Trade Receivables - Current", date) +
            getValue("Cash & Cash Equivalents", date) +
            getValue("Bank balances other than above", date) +
            getValue("Loans - Current", date) +
            getValue("Others - Current", date) +
            getValue("Current Tax Assets", date) +
            getValue("Other Current Assets", date);
        });
        const ialCAssets: BalanceSheetItem = {
          name: "2.  Current Assets",
          values: currentAssetsValuesInd,
          isTotal: true,
          level: 1,
        };

        const ialTotAssets = mkTotal("TOTAL ASSETS", "Total Assets", 1);

        const assetsItems: BalanceSheetItem[] = [
          ialNcAssets, ialPPE, ialCWIP, ialInvProp, ialGW, ialOIA, ialIUD, ialBio,
          ialNcFinAH, ialInvNc, ialTrRNc, ialLoNc, ialOtNc, ialDTA, ialONcA,
          ialCAssets, ialInv,
          ialCFinAH, ialInvC, ialTrRC, ialCash, ialBankBal, ialLoC, ialOtC,
          ialCTA, ialOCA,
        ];

        // CAGR + trend enrichment (same as AS)
        const bsCagrInd = (data as any).analytics?.bs?.cagr as { [key: string]: number | null } | undefined;
        [...equityAndLiabilitiesItems, ialTotLiab, ...assetsItems, ialTotAssets].forEach((item) => {
          item.cagr = item.key != null && bsCagrInd?.[item.key] != null ? bsCagrInd[item.key]! : null;
        });
        const chronologicalDatesInd = [...allDates].reverse();
        const setTrendInd = (item: BalanceSheetItem) => {
          if (chronologicalDatesInd.length < 2) { item.trend = null; return; }
          const fv = item.values[chronologicalDatesInd[0]];
          const lv = item.values[chronologicalDatesInd[chronologicalDatesInd.length - 1]];
          item.trend = (typeof fv === "number" && typeof lv === "number")
            ? lv > fv ? "Up" : lv < fv ? "Down" : "Stable"
            : null;
        };
        [...equityAndLiabilitiesItems, ialTotLiab, ...assetsItems, ialTotAssets].forEach(setTrendInd);

        setEquityAndLiabilities({ title: "Equity & Liabilities", items: equityAndLiabilitiesItems, total: ialTotLiab });
        setAssets({ title: "Assets", items: assetsItems, total: ialTotAssets });

      } else {
        // ════════════════════════════════════════════════════════════════════
        // AS path (existing logic)
        // ════════════════════════════════════════════════════════════════════
        setDates(allDates);

        const createItem = (
          name: string,
          key: string,
          isSubItem = false,
          isTotal = false,
          level = 1
        ): BalanceSheetItem => {
          const values: { [date: string]: number } = {};
          allDates.forEach((date) => { values[date] = getValue(key, date); });
          return { name, key, values, isSubItem, isTotal, level };
        };

        // ── Equity & Liabilities ─────────────────────────────────────────
        const shareCapital = createItem("a. Share Capital", "Share Capital", true, false, 2);
        const reservesSurplus = createItem("b. Reserves & Surplus", "Reserves & Surplus", true, false, 2);
        const moneyReceivedWarrants = createItem(
          "c. Money Received Against Share Warrants",
          "Money Received Against Share Warrants",
          true, false, 2
        );

        const shareholderFundValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          shareholderFundValues[date] =
            getValue("Share Capital", date) +
            getValue("Reserves & Surplus", date) +
            getValue("Money Received Against Share Warrants", date);
        });
        const shareholdersFunds: BalanceSheetItem = {
          name: "1. Shareholders' Funds",
          values: shareholderFundValues,
          isTotal: true,
          level: 1,
        };

        const shareApplicationMoney = createItem(
          "2. Share Application Money Pending Allotment",
          "Share Application Money Pending Allotment",
          false, false, 1
        );

        const longTermBorrowings = createItem("a. Long-term borrowings", "Long-term borrowings", true, false, 2);
        const deferredTaxLiabilities = createItem("b. Deferred Tax Liabilities", "Deferred Tax Liabilities", true, false, 2);
        const otherLongTermLiabilities = createItem("c. Other Long-term Liabilities", "Other long-term Liabilities", true, false, 2);
        const longTermProvisions = createItem("d. Long-term Provisions", "Long-term Provisions", true, false, 2);

        const nonCurrentLiabilitiesValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          nonCurrentLiabilitiesValues[date] =
            getValue("Long-term borrowings", date) +
            getValue("Deferred Tax Liabilities", date) +
            getValue("Other long-term Liabilities", date) +
            getValue("Long-term Provisions", date);
        });
        const nonCurrentLiabilities: BalanceSheetItem = {
          name: "3. Non-Current Liabilities",
          values: nonCurrentLiabilitiesValues,
          isTotal: true,
          level: 1,
        };

        const shortTermBorrowings = createItem("a. Short-term borrowings", "Short-term borrowings", true, false, 2);
        const tradeMsme = createItem("b. Trade Payables - MSME", "Total outstanding dues of MSME", true, false, 2);
        const tradeOthers = createItem(
          "c. Trade Payables - Others",
          "Total outstanding dues of creditors other than MSME",
          true, false, 2
        );
        const otherCurrentLiabilities = createItem("d. Other Current Liabilities", "Other current liabilities", true, false, 2);
        const shortTermProvisions = createItem("e. Short-term Provisions", "Short-term Provisions", true, false, 2);

        const currentLiabilitiesValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          currentLiabilitiesValues[date] =
            getValue("Short-term borrowings", date) +
            getValue("Total outstanding dues of MSME", date) +
            getValue("Total outstanding dues of creditors other than MSME", date) +
            getValue("Other current liabilities", date) +
            getValue("Short-term Provisions", date);
        });
        const currentLiabilities: BalanceSheetItem = {
          name: "4. Current Liabilities",
          values: currentLiabilitiesValues,
          isTotal: true,
          level: 1,
        };

        const totalLiabilitiesValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          totalLiabilitiesValues[date] = data[date]?.bs_as?.["Total Liabilities"] ?? 0;
        });
        const totalLiabilities: BalanceSheetItem = {
          name: "TOTAL LIABILITIES",
          key: "Total Liabilities",
          values: totalLiabilitiesValues,
          isTotal: true,
          level: 1,
        };

        const equityAndLiabilitiesItems: BalanceSheetItem[] = [
          shareholdersFunds,
          shareCapital,
          reservesSurplus,
          moneyReceivedWarrants,
          shareApplicationMoney,
          nonCurrentLiabilities,
          longTermBorrowings,
          deferredTaxLiabilities,
          otherLongTermLiabilities,
          longTermProvisions,
          currentLiabilities,
          shortTermBorrowings,
          tradeMsme,
          tradeOthers,
          otherCurrentLiabilities,
          shortTermProvisions,
        ];

        // ── Assets ───────────────────────────────────────────────────────
        const tangibleAssets = createItem("(i) Tangible Assets", "Tangible Assets", true, false, 3);
        const intangibleAssets = createItem("(ii) Intangible Assets", "Intangible Assets", true, false, 3);
        const capitalWorkProgress = createItem("(iii) Capital Work-in-Progress", "Capital work-in progress", true, false, 3);
        const intangibleUnderDev = createItem("(iv) Intangible Assets Under Development", "Intangible Assets Under Development", true, false, 3);

        const ppeValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          ppeValues[date] =
            getValue("Tangible Assets", date) +
            getValue("Intangible Assets", date) +
            getValue("Capital work-in progress", date) +
            getValue("Intangible Assets Under Development", date);
        });
        const propertyPlantEquipment: BalanceSheetItem = {
          name: "a. Property, Plant & Equipment",
          values: ppeValues,
          isSubItem: true,
          level: 2,
        };

        const nonCurrentInvestments = createItem("b. Non-Current Investments", "Non-Current Investments", true, false, 2);
        const deferredTaxAssetsItem = createItem("c. Deferred Tax Assets", "Deferred Tax Assets", true, false, 2);
        const longTermLoansAdvances = createItem("d. Long-term Loans & Advances", "Long-term Loans & Advances", true, false, 2);
        const otherNonCurrentAssets = createItem("e. Other Non-current Assets", "Other non-current Assets", true, false, 2);

        const nonCurrentAssetsValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          nonCurrentAssetsValues[date] =
            ppeValues[date] +
            getValue("Non-Current Investments", date) +
            getValue("Deferred Tax Assets", date) +
            getValue("Long-term Loans & Advances", date) +
            getValue("Other non-current Assets", date);
        });
        const nonCurrentAssets: BalanceSheetItem = {
          name: "1. Non-Current Assets",
          values: nonCurrentAssetsValues,
          isTotal: true,
          level: 1,
        };

        const currentInvestments = createItem("a. Current Investments", "Current Investments", true, false, 2);
        const inventories = createItem("b. Inventories", "Inventories", true, false, 2);
        const tradeReceivables = createItem("c. Trade Receivables", "Trade Receivables", true, false, 2);
        const cashEquivalents = createItem("d. Cash & Cash Equivalents", "Cash & Cash Equivalents", true, false, 2);
        const shortTermLoansAdvances = createItem("e. Short-term Loans & Advances", "Short-term Loans & Advances", true, false, 2);
        const otherCurrentAssets = createItem("f. Other Current Assets", "Other Current Assets", true, false, 2);

        const currentAssetsValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          currentAssetsValues[date] =
            getValue("Current Investments", date) +
            getValue("Inventories", date) +
            getValue("Trade Receivables", date) +
            getValue("Cash & Cash Equivalents", date) +
            getValue("Short-term Loans & Advances", date) +
            getValue("Other Current Assets", date);
        });
        const currentAssets: BalanceSheetItem = {
          name: "2. Current Assets",
          values: currentAssetsValues,
          isTotal: true,
          level: 1,
        };

        const totalAssetsValues: { [date: string]: number } = {};
        allDates.forEach((date) => {
          totalAssetsValues[date] = data[date]?.bs_as?.["Total Assets"] ?? 0;
        });
        const totalAssets: BalanceSheetItem = {
          name: "TOTAL ASSETS",
          key: "Total Assets",
          values: totalAssetsValues,
          isTotal: true,
          level: 1,
        };

        const assetsItems: BalanceSheetItem[] = [
          nonCurrentAssets,
          propertyPlantEquipment,
          tangibleAssets,
          intangibleAssets,
          capitalWorkProgress,
          intangibleUnderDev,
          nonCurrentInvestments,
          deferredTaxAssetsItem,
          longTermLoansAdvances,
          otherNonCurrentAssets,
          currentAssets,
          currentInvestments,
          inventories,
          tradeReceivables,
          cashEquivalents,
          shortTermLoansAdvances,
          otherCurrentAssets,
        ];

        // ── Enrich with CAGR + trend ──────────────────────────────────
        const bsCagr = (data as any).analytics?.bs?.cagr as { [key: string]: number | null } | undefined;
        [...equityAndLiabilitiesItems, ...assetsItems].forEach((item) => {
          item.cagr = item.key != null && bsCagr?.[item.key] != null ? bsCagr[item.key]! : null;
        });

        const chronologicalDates = [...allDates].reverse();
        const setTrend = (item: BalanceSheetItem) => {
          if (chronologicalDates.length < 2) { item.trend = null; return; }
          const first = chronologicalDates[0];
          const last = chronologicalDates[chronologicalDates.length - 1];
          const fv = item.values[first];
          const lv = item.values[last];
          if (typeof fv === "number" && typeof lv === "number") {
            item.trend = lv > fv ? "Up" : lv < fv ? "Down" : "Stable";
          } else {
            item.trend = null;
          }
        };
        [...equityAndLiabilitiesItems, totalLiabilities, ...assetsItems, totalAssets].forEach(setTrend);

        setEquityAndLiabilities({ title: "Equity & Liabilities", items: equityAndLiabilitiesItems, total: totalLiabilities });
        setAssets({ title: "Assets", items: assetsItems, total: totalAssets });
      }

      setDataLoaded(true);
    } catch (err) {
      console.error("Failed to load Balance Sheet data:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFinancialData();
  }, []);

  // ── AS renderer (unchanged) ───────────────────────────────────────────────
  const datesChronological = [...dates].reverse();
  const renderASSection = (section: BalanceSheetSection, isLiabilitySection: boolean) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="sticky left-0 z-20 min-w-[220px] w-[220px] max-w-[220px] bg-[var(--card)] border-r border-white/10 font-medium text-white/60">
              Particulars
            </TableHead>
            {dates.map((date) => (
              <TableHead
                key={date}
                className="font-semibold text-white text-center min-w-[120px] whitespace-nowrap border-white/10"
              >
                {date}
              </TableHead>
            ))}
            <TableHead className="sticky right-[80px] z-20 min-w-[80px] w-[80px] max-w-[80px] bg-[var(--card)] border-l border-white/10 font-medium text-white/60 text-right">
              CAGR
            </TableHead>
            <TableHead className="sticky right-0 z-20 min-w-[80px] w-[80px] max-w-[80px] bg-[var(--card)] border-l border-white/10 font-medium text-white/60 text-right">
              Trend
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {section.items.map((item, index) => {
            const cellClass = `py-1 ${item.isTotal ? "font-bold border-t border-white/10 pt-2" : ""}`;
            const labelClass = item.isTotal
              ? "font-bold text-white"
              : item.isSubItem
                ? "text-sm text-white/90"
                : "font-medium text-white";
            return (
              <TableRow key={index} className="border-white/10 hover:bg-transparent">
                <TableCell
                  className={`sticky left-0 z-10 bg-[var(--card)] border-r border-white/10 ${cellClass}`}
                  style={{ paddingLeft: `${12 + (item.level || 1) * 16}px` }}
                >
                  <span className={labelClass}>{item.name}</span>
                </TableCell>
                {dates.map((date) => {
                  const show = item.values[date] === 0 && !item.isTotal ? "—" : formatCrores(item.values[date]);
                  return (
                    <TableCell
                      key={date}
                      className={`font-mono text-right min-w-[120px] text-white ${cellClass} ${item.isTotal ? "font-bold" : ""} border-white/10`}
                    >
                      {show}
                    </TableCell>
                  );
                })}
                <TableCell className={`sticky right-[80px] z-10 bg-[var(--card)] border-l border-white/10 text-right font-mono text-white ${cellClass}`}>
                  {item.cagr != null ? `${(item.cagr * 100).toFixed(2)}%` : "—"}
                </TableCell>
                <TableCell className={`sticky right-0 z-10 bg-[var(--card)] text-right text-white ${cellClass}`}>
                  <TrendSparkline
                    values={item.values}
                    datesChronological={datesChronological}
                    trend={item.trend}
                    isLiability={isLiabilitySection}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow className="border-white/10 hover:bg-transparent border-t-2 border-white/20">
            <TableCell className="sticky left-0 z-10 bg-[var(--card)] border-r border-white/10 font-bold text-white py-2">
              {section.total.name}
            </TableCell>
            {dates.map((date) => (
              <TableCell key={date} className="font-mono text-right min-w-[120px] text-white font-bold border-white/10 py-2">
                {formatCrores(section.total.values[date])}
              </TableCell>
            ))}
            <TableCell className="sticky right-[80px] z-10 bg-[var(--card)] border-l border-white/10 text-right font-mono text-white py-2">
              {section.total.cagr != null ? `${(section.total.cagr * 100).toFixed(2)}%` : "—"}
            </TableCell>
            <TableCell className="sticky right-0 z-10 bg-[var(--card)] text-right py-2">
              <TrendSparkline
                values={section.total.values}
                datesChronological={datesChronological}
                trend={section.total.trend}
                isLiability={isLiabilitySection}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );

  // ── Ind AS renderer ───────────────────────────────────────────────────────
  const stickyCellBg = "bg-[var(--card)]";
  const renderIndASTable = (rows: IndASRow[], sectionTitle: string) => {
    const [d1, d2] = indAsDates; // d1 = latest, d2 = previous

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className={`sticky left-0 z-20 w-[200px] max-w-[200px] min-w-[200px] ${stickyCellBg} border-r border-white/10 font-medium text-white/60 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.2)]`}>
                Particulars
              </TableHead>
              {d1 && (
                <TableHead className="font-semibold text-white text-right min-w-[120px] max-w-[160px] whitespace-normal break-words border-white/10">
                  As at {d1}
                </TableHead>
              )}
              {d2 && (
                <TableHead className="font-semibold text-white text-right min-w-[120px] max-w-[160px] whitespace-normal break-words border-white/10">
                  As at {d2}
                </TableHead>
              )}
              <TableHead className="font-semibold text-white text-right min-w-[100px] whitespace-nowrap border-white/10">
                Variance
              </TableHead>
              <TableHead className="font-semibold text-white text-right min-w-[90px] whitespace-nowrap border-white/10">
                Variance %
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, idx) => {
              const v1 = d1 ? (row.values[d1] ?? 0) : 0;
              const v2 = d2 ? (row.values[d2] ?? 0) : 0;
              const variance = v1 - v2;
              const isSubTotal = row.type === "subTotal";
              const isGrandTotal = row.type === "grandTotal";
              const isSectionHeader = row.type === "sectionHeader";
              const isSubGroupHeader = row.type === "subGroupHeader";
              const isBlank = row.type === "blank";
              const hasValue = row.type === "lineItem" || isSubTotal || isGrandTotal;

              if (isBlank) {
                return (
                  <TableRow key={idx} className="border-0 hover:bg-transparent h-3">
                    <TableCell className={`sticky left-0 z-20 w-[200px] max-w-[200px] min-w-[200px] p-0 ${stickyCellBg} border-r border-white/10 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.2)]`} />
                    <TableCell className="p-0 border-white/10" />
                    <TableCell className="p-0 border-white/10" />
                    <TableCell className="p-0 border-white/10" />
                    <TableCell className="p-0 border-white/10" />
                  </TableRow>
                );
              }

              // Style classes per row type
              const rowBorder = isGrandTotal
                ? "border-t-2 border-white/30"
                : isSubTotal
                  ? "border-t border-white/20"
                  : "border-white/10";

              const nameCellStyle: React.CSSProperties = {
                paddingLeft: `${8 + row.level * 20}px`,
              };

              const nameClass = isGrandTotal
                ? "font-bold text-white text-sm uppercase tracking-wide break-words"
                : isSectionHeader
                  ? "font-bold text-white text-sm break-words"
                  : isSubGroupHeader
                    ? "font-semibold text-white/80 text-sm break-words"
                    : isSubTotal
                      ? "text-white/0 select-none" // invisible — no label for subtotal row
                      : "text-white/90 text-sm break-words";

              const valueCellClass = `font-mono text-right text-sm whitespace-nowrap ${
                isGrandTotal ? "font-bold text-white" : isSubTotal ? "font-semibold text-white" : "text-white/90"
              }`;

              const varianceColor =
                !hasValue || variance === 0
                  ? "text-white/60"
                  : variance > 0
                    ? "text-emerald-400"
                    : "text-red-400";

              return (
                <TableRow
                  key={idx}
                  className={`hover:bg-white/5 ${rowBorder}`}
                >
                  {/* Particulars */}
                  <TableCell
                    className={`sticky left-0 z-20 w-[200px] max-w-[200px] min-w-[200px] ${stickyCellBg} border-r border-white/10 py-1.5 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.2)] align-top whitespace-normal`}
                    style={nameCellStyle}
                  >
                    <span className={`${nameClass} block min-w-0 whitespace-normal break-words`}>{row.name}</span>
                  </TableCell>

                  {/* As at d1 */}
                  <TableCell className={`${valueCellClass} py-1.5 border-white/10`}>
                    {hasValue ? (v1 === 0 && !isSubTotal && !isGrandTotal ? "—" : formatCrores(v1)) : ""}
                  </TableCell>

                  {/* As at d2 */}
                  <TableCell className={`${valueCellClass} py-1.5 border-white/10`}>
                    {hasValue && d2 ? (v2 === 0 && !isSubTotal && !isGrandTotal ? "—" : formatCrores(v2)) : ""}
                  </TableCell>

                  {/* Variance */}
                  <TableCell className={`font-mono text-right text-sm py-1.5 whitespace-nowrap border-white/10 ${hasValue && d2 ? varianceColor : ""}`}>
                    {hasValue && d2
                      ? variance === 0
                        ? "—"
                        : `${variance > 0 ? "+" : ""}${formatCrores(variance)}`
                      : ""}
                  </TableCell>

                  {/* Variance % */}
                  <TableCell className={`font-mono text-right text-sm py-1.5 whitespace-nowrap border-white/10 ${hasValue && d2 ? varianceColor : ""}`}>
                    {hasValue && d2 && (v1 !== 0 || v2 !== 0)
                      ? formatVariancePct(v1, v2)
                      : ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  // ── Loading / error states ────────────────────────────────────────────────
  if (!dataLoaded && !loading) {
    return (
      <div className="flex h-full items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-white mb-6">Balance Sheet</h2>
          <p className="text-white/80 text-lg">Close Trial Balance for these pages to load</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
          <span className="text-white">Loading Balance Sheet data…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-400 mb-2">Error loading data</div>
          <div className="text-sm text-white/60 mb-4">{error}</div>
          <Button
            onClick={loadFinancialData}
            className="mt-4 bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // ── Shared UI (AS and Ind AS) ─────────────────────────────────────────────
  return (
    <div className="space-y-6 text-white">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Balance Sheet</h2>
        <p className="text-white/60">{companyName}</p>
        {frf === "Indian Accounting Standards (Ind AS)" ? (
          <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
            Indian Accounting Standards (Ind AS)
          </span>
        ) : frf ? (
          <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
            {frf}
          </span>
        ) : null}
      </div>

      {equityAndLiabilities && assets && (
        <div className="space-y-6">
          {frf === "Indian Accounting Standards (Ind AS)" ? (
            <>
              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Assets</CardTitle>
                </CardHeader>
                <CardContent>
                  {renderASSection(assets, false)}
                </CardContent>
              </Card>

              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Equity &amp; Liabilities</CardTitle>
                </CardHeader>
                <CardContent>
                  {renderASSection(equityAndLiabilities, true)}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Equity &amp; Liabilities</CardTitle>
                </CardHeader>
                <CardContent>
                  {renderASSection(equityAndLiabilities, true)}
                </CardContent>
              </Card>

              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Assets</CardTitle>
                </CardHeader>
                <CardContent>
                  {renderASSection(assets, false)}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
